import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  index: number;
  isInView: boolean;
}
function FAQItem({
  question,
  answer,
  index,
  isInView
}: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.6,
    delay: 0.1 + index * 0.1
  }} className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-2">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 md:px-8 py-6 text-left flex items-center justify-between gap-4 hover:bg-[#F5F1E8] transition-colors duration-200" aria-expanded={isOpen} data-id="element-3">
        <span className="text-lg md:text-xl font-semibold text-[#3A3A3A]" data-id="element-4">
          {question}
        </span>
        <ChevronDownIcon className={`w-6 h-6 text-[#7B9FAB] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-5" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-6">
        <div className="px-6 md:px-8 pb-6 text-[#5A5A5A] leading-relaxed" data-id="element-7">
          {answer}
        </div>
      </motion.div>
    </motion.div>;
}
export function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const faqs = [{
    question: '¿Y si no tengo 15 minutos?',
    answer: 'Usas el modo 5 minutos. El punto es sostener el hilo. Incluso 5 minutos de reset visible evitan que el caos se acumule y te abrume. El sistema está diseñado para adaptarse a tu energía real, no a una versión ideal de ti.'
  }, {
    question: '¿Y si vivo con gente que desordena?',
    answer: 'Este sistema no intenta controlar a otros. Te devuelve control diario en zonas clave. No es sobre cambiar a tu familia, es sobre tener un método que funcione para ti, independientemente de lo que hagan los demás. Tu reset es tuyo.'
  }, {
    question: '¿Tengo que imprimir?',
    answer: 'No. Puedes usarlo desde el teléfono. Imprimir solo lo hace más fácil si prefieres tener las misiones a la vista. El PDF está diseñado para funcionar de ambas formas: digital o impreso.'
  }, {
    question: '¿Esto realmente funciona si estoy muy cansada?',
    answer: 'Sí, porque está diseñado específicamente para cuando estás cansada. No es un maratón de limpieza. Son 15 minutos con una misión clara. Tu cerebro no negocia porque sabe exactamente qué hacer y cuándo parar.'
  }, {
    question: '¿Qué pasa si me salto días?',
    answer: 'No pasa nada. El sistema incluye un "Plan de rescate" para cuando la vida se pone imposible. No hay culpa, no hay perfección. Solo vuelves cuando puedas. El tracker te ayuda a retomar sin drama.'
  }];
  return <section ref={ref} className="w-full bg-[#F5F1E8] py-20 md:py-32 px-4" data-id="element-8">
      <div className="max-w-4xl mx-auto" data-id="element-9">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12" data-id="element-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#3A3A3A] mb-4 leading-tight" data-id="element-11">
            Preguntas frecuentes
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-12">
          {faqs.map((faq, index) => <FAQItem key={faq.question} question={faq.question} answer={faq.answer} index={index} isInView={isInView} data-id="element-13" />)}
        </div>
      </div>
    </section>;
}